import { createNlsMiddleware, lccstate, searchOpenProcess, userstate } from 'lcc-state';
import { logSystem } from 'logger';
import { publish, subscribe } from 'pub-sub';
import { createBreakpointMiddleware } from '@dojo/framework/core/middleware/breakpoint';
import { focus } from '@dojo/framework/core/middleware/focus';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { create, v, w } from '@dojo/framework/core/vdom';
import Avatar from '@dojo/widgets/avatar';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { theme } from '@dojo/widgets/middleware/theme';
import Icon from '@holmescorp/base-widgets/icon';
import css from '../css/heading.m.css';
const breakpoint = createBreakpointMiddleware({ mobile: 0, fullsize: 769 });
const icache = createICacheMiddleware();
const nls = createNlsMiddleware('settings');
const factory = create({
    icache,
    theme,
    nls,
    lccstate,
    userstate,
    breakpoint,
    focus
}).properties();
export const AlternateHeading = factory(({ middleware: { icache, theme, nls, lccstate, breakpoint, focus, userstate } }) => {
    const onlinestate = icache.getOrSet('onlinestate', false);
    const { get, path } = lccstate;
    const { get: uget, path: upath } = userstate;
    const online = get(path('online'));
    const product = get(path('product'));
    const mainmenu = get(path('mainmenu'));
    const bp = breakpoint.get('header');
    const isMobile = bp && bp.breakpoint === 'mobile';
    const currentLocation = uget(upath('location'));
    const userFullname = uget(upath('fullname'));
    const userInitials = userFullname
        .split(' ')
        .map((name) => name[0].toUpperCase())
        .join('') || w(Icon, { faname: 'user' });
    if (onlinestate !== online) {
        icache.set('onlinestate', online);
        logSystem.info(() => `${online ? 'WS online' : 'WS offline'}`);
    }
    const { isPlaceholder } = nls();
    if (isPlaceholder) {
        return null;
    }
    const themeCss = theme.classes(css);
    const mobile = bp && bp.breakpoint === 'mobile' ? '-mobile' : '';
    const productimg = `${window.hcstatic.portal_cdn}/js/images/${product}${mobile}.png`;
    const title = v('img', {
        classes: [themeCss.product],
        ...formatAriaProperties({ label: product }),
        role: 'img',
        xqa: 'product',
        alt: product,
        src: productimg,
        onclick: (event) => {
            event && event.stopPropagation();
            publish('site/homepage');
        }
    });
    if (mainmenu.length === 0) {
        mainmenu.push({
            pane: { widget: 'GlossaryPane', params: {} },
            icon: { action: 'glossary/open', faname: 'briefcase', altText: 'glossary', ttText: 'Glossary' }
        });
        mainmenu.push({
            pane: { widget: 'SettingsPane', params: {} },
            icon: { action: 'settings/open', faname: 'cogs', altText: 'settings', ttText: 'Settings' }
        });
    }
    subscribe('search/open', () => {
        lccstate.run(searchOpenProcess)({ searchOpen: true });
    });
    const menupanes = [
        w('VideoPane', { videonum: '1', openaction: 'video/open' }),
        w('CertPane', {}),
        w('EvalPane', {}),
        w('MobileNavPane', {})
    ];
    mainmenu
        .filter((item) => !('splash' in item))
        .forEach((item) => {
        const { pane } = item;
        if (pane) {
            const { widget, params } = pane;
            menupanes.push(w(widget, Object.assign({
                onClose: () => {
                    focus.focus();
                }
            }, params)));
        }
    });
    const logoTitle = v('div', { classes: [themeCss.logo] }, [
        v('div', { classes: [themeCss.title], key: 'title', xqa: 'nav-logo-title' }, [title])
    ]);
    const searchBox = v('div', { classes: [themeCss.primary] }, [
        v('div', {
            classes: [themeCss.search],
            key: 'search',
            xqa: 'nav-search-button',
            onclick: () => publish('search/open')
        }, [w(Icon, { faname: 'search', size: 'lg' })])
    ]);
    const navigation = isMobile
        ? v('div', {
            onclick: () => publish('mobile-nav/open'),
            xqa: 'nav-mobile-open-button'
        }, [w(Icon, { faname: 'bars' })])
        : v('div', { classes: [themeCss.primary, themeCss.nav] }, [
            v('span', {
                xqa: 'nav-home-button',
                classes: [currentLocation === '/explore' ? themeCss.active : '', themeCss.navItem],
                onclick: () => publish('site/homepage')
            }, ['Home']),
            v('span', {
                xqa: 'nav-my-progress-button',
                classes: [currentLocation === '/my-progress' ? themeCss.active : '', themeCss.navItem],
                onclick: () => publish('site/progress')
            }, ['My Progress'])
        ]);
    const user = v('div', { classes: [themeCss.primary] }, [
        v('span', {
            classes: [themeCss.avatar, themeCss.navItem],
            xqa: 'avatar',
            onclick: () => publish('settings/open')
        }, [w(Avatar, { size: 'large' }, [userInitials])])
    ]);
    return v('header', {
        key: 'header',
        role: 'banner',
        xqa: 'heading',
        classes: [theme.variant(), themeCss.root]
    }, [
        v('div', { classes: [themeCss.heading], key: 'root' }, [
            isMobile
                ? v('div', { classes: [themeCss.row] }, [navigation, logoTitle, searchBox, user])
                : v('div', { classes: [themeCss.row] }, [
                    logoTitle,
                    v('div', { classes: [themeCss.actions] }, [navigation, searchBox, user])
                ])
        ]),
        ...menupanes
    ]);
});
